import React, { useRef, useEffect } from "react";

import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import {
  selectIntroPopupActive,
  selectSessionExpired,
  selectShowGameAfterTransition,
  selectFreeRoundsStatus,
  selectFreeRoundsCompletionPending,
  selectIsFreeRoundsCompletionReload,
  selectMenuActive,
} from "../../../State/selectors";

import ToolbarTop from "./Toolbars/ToolbarTop";
import ToolbarBottom from "./Toolbars/ToolbarBottom";
import ToolbarBottomSecond from "./Toolbars/ToolbarBottomSecond";
import ToolbarBackgrounds from "./Toolbars/ToolbarBackgrounds";
import BetSelectPopup from "./Popups/BetSelectPopup";
import AutoplayPopup from "./Popups/AutoplayPopup";
import WarningPopup from "./Popups/WarningPopup";
import InfoPopup from "./Popups/InfoPopup";
import MenuOverlay from "./Menu/MenuOverlay";
import TurboSuggestionPopup from "./Popups/TurboSuggestionPopup";
import LeaveGamePopup from "./Popups/LeaveGamePopup";
import BuyBonusPopup from "./Popups/BuyBonusPopup";
import FreeRoundsCompletionHandler from "../../FreeRounds/FreeRoundsCompletionHandler";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import BackgroundDecoration from "../../Animations/BackgroundDecoration/BackgroundDecoration";
import anime from "animejs";


const UserInterface = ({ children }) => {
  const containerRef = useRef(null);
  const animRef = useRef(null);
  const introPopupActive = useSelector(selectIntroPopupActive);
  const sessionExpired = useSelector(selectSessionExpired);
  const showGameAfterTransition = useSelector(selectShowGameAfterTransition);
  const menuActive = useSelector(selectMenuActive);

  const freeRoundsStatus = useSelector(selectFreeRoundsStatus);
  const completionPending = useSelector(selectFreeRoundsCompletionPending);
  const isFreeRoundsCompletionReload = useSelector(
    selectIsFreeRoundsCompletionReload,
  );

  // Only render completion handler when free rounds feature is enabled and free rounds are active or completing
  const shouldRenderCompletionHandler =
    FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
    (freeRoundsStatus === "active" ||
      freeRoundsStatus === "played" ||
      completionPending ||
      isFreeRoundsCompletionReload);

  // Fade entire game UI when menu opens/closes
  const fadeDelayRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    // Don't animate when intro popup or session expired controls alpha
    if (introPopupActive || sessionExpired) return;

    if (fadeDelayRef.current) {
      clearTimeout(fadeDelayRef.current);
      fadeDelayRef.current = null;
    }

    if (animRef.current) animRef.current.pause();

    if (menuActive) {
      // Fade out immediately when menu opens
      animRef.current = anime({
        targets: containerRef.current,
        alpha: 0,
        duration: 200,
        easing: "easeInQuad",
      });
    } else {
      // Delay fade-in to start when overlay begins fading (after content fade-out)
      fadeDelayRef.current = setTimeout(() => {
        animRef.current = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          easing: "easeOutQuad",
        });
      }, 200);
    }
  }, [menuActive, introPopupActive, sessionExpired]);

  const getInterfaceAlpha = () => {
    if (showGameAfterTransition) {
      return 1;
    } else if (introPopupActive || sessionExpired) {
      return 0.001;
    } else {
      return 1;
    }
  };

  const interfaceAlpha = getInterfaceAlpha();

  return (
    <>
      <Container
        ref={containerRef}
        name="primary_user_interface"
        sortableChildren
        alpha={interfaceAlpha}
      >
        {/* Layer 1: Toolbar backgrounds only (behind game) */}
        <ToolbarBackgrounds />

        {/* Layer 2: Game container (renders over backgrounds, effects bleed over) */}
        {children}

        {/* Layer 2.5: Background decoration (tree/flowers) — above game/deer, below toolbars */}
        <BackgroundDecoration />

        {/* Layer 3: Toolbar icons/buttons (on top of game) */}
        <ToolbarTop />
        <ToolbarBottomSecond />
        <ToolbarBottom />
      </Container>

      {/* Render popups OUTSIDE the main container so they're always visible */}

      <WarningPopup />
      <InfoPopup />
      <MenuOverlay />
      <BetSelectPopup />
      <AutoplayPopup />
      <TurboSuggestionPopup />
      <LeaveGamePopup />
      {FEATURES_CONFIG.BONUS_BUY.ENABLED && <BuyBonusPopup />}

      {/* Free rounds completion handler - only render when free rounds are active/completing */}
      {shouldRenderCompletionHandler && <FreeRoundsCompletionHandler />}
    </>
  );
};

export default UserInterface;
